import { FormEvent, useState } from "react";
import { createPortal } from "react-dom";
import { useAuth0 } from "@auth0/auth0-react";
import { Button } from "@/components/UI/Button";
import InfoTooltip from "@/components/UI/InfoTooltip";
import { closeLg } from "@/components/icons/SVGIcons";
import {
  HELPDESK_SEVERITY_OPTIONS,
  HelpdeskSeverity,
} from "@/lib/interfaces and Enums/ENUMHelpdeskSeverity";
import { submitHelpdeskTicket } from "@/store/helpdeskStoreACTIONS";

const SEVERITY_TOOLTIP_MESSAGE = (
  <div className="flex flex-col gap-1.5">
    <div>
      <b>Low</b> - A minor issue or general question. Doesn't stop you from
      working. Examples: a cosmetic glitch, a "how do I..." question, a
      feature request.
    </div>
    <div>
      <b>Medium</b> - Something's not working right, but you have a
      workaround or it only affects one task. Examples: a report won't
      export, a setting keeps resetting, a non-critical tool is slow.
    </div>
    <div>
      <b>High</b> - A core feature is broken or unavailable and it's
      affecting your ability to work. No workaround available. Examples:
      someone can't log in, data loss, unable to complete trade.
    </div>
    <div>
      <b>Critical</b> - A complete outage or trade-stopping issue affecting
      multiple people, or anything involving data loss or security.
      Examples: the whole platform is down, sensitive data is exposed,
      security incident.
    </div>
  </div>
);

const HelpdeskForm = ({ onClose }: { onClose: () => void }) => {
  const { getAccessTokenSilently } = useAuth0();
  const [severity, setSeverity] = useState<HelpdeskSeverity>("Low");
  const [message, setMessage] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setIsSubmitting(true);
    const success = await submitHelpdeskTicket({
      severity,
      message,
      getAccessTokenSilently,
    });
    setIsSubmitting(false);
    if (success) {
      onClose();
    }
  };

  // Wrapping the return in a hidden div means it will never alter the DOM where it is loaded.
  return (
    <div className="hidden">
      {createPortal(
        <div
          id="helpdeskFormBackground"
          className="absolute top-0 z-40 flex h-screen w-full"
          style={{ backgroundColor: "rgb(203, 213, 225, 0.2)" }}
          onClick={onClose}
        >
          <div className="flex h-full w-full items-center justify-center">
            <div
              data-testid="HelpdeskFormContainer"
              id="HelpdeskFormContainer"
              className="w-[440px] rounded-2xl border border-gray-300 bg-cubeBase-500 text-black dark:border-transparent dark:bg-cubeBaseDark-500 dark:text-white"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex flex-col items-center justify-between rounded-xl drop-shadow-lg">
                <div
                  id="HelpdeskFormHeader"
                  className="flex h-9 w-full flex-row items-center justify-between rounded-t-xl border-b border-gray-600 bg-cubeBase-800 px-4 text-black dark:bg-cubeBaseDark-900 dark:text-white"
                >
                  <div className="text-md overflow-hidden">
                    Email Helpdesk
                  </div>
                  <Button
                    variant="exit"
                    id="HelpdeskFormCloseButton"
                    onClick={onClose}
                  >
                    {closeLg}
                  </Button>
                </div>
                <form
                  onSubmit={handleSubmit}
                  className="flex w-full flex-col gap-3 p-4"
                >
                  <label className="flex flex-col gap-1 text-sm">
                    <span className="flex items-center">
                      Severity
                      <InfoTooltip
                        id="HelpdeskSeverityTooltip"
                        message={SEVERITY_TOOLTIP_MESSAGE}
                        maxWidthClass="max-w-[320px]"
                        tooltipClassName="!bg-white !text-black !border !border-gray-300 dark:!bg-[#222] dark:!text-white dark:!border-none"
                      />
                    </span>
                    <select
                      id="HelpdeskFormSeveritySelect"
                      data-testid="HelpdeskFormSeveritySelect"
                      value={severity}
                      onChange={(e) =>
                        setSeverity(e.target.value as HelpdeskSeverity)
                      }
                      className="h-[28px] w-full rounded-md border border-gray-300 bg-transparent px-1 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-200"
                    >
                      {HELPDESK_SEVERITY_OPTIONS.map((option) => (
                        <option key={option} value={option}>
                          {option}
                        </option>
                      ))}
                    </select>
                  </label>
                  <label className="flex flex-col gap-1 text-sm">
                    Description
                    <textarea
                      id="HelpdeskFormMessageTextarea"
                      data-testid="HelpdeskFormMessageTextarea"
                      value={message}
                      onChange={(e) => setMessage(e.target.value)}
                      rows={5}
                      placeholder="Describe the issue..."
                      className="w-full resize-none rounded-md border border-gray-300 bg-transparent p-2 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-200"
                    />
                  </label>
                  <div className="flex justify-end gap-2 pt-1">
                    <Button
                      type="button"
                      id="HelpdeskFormCancelButton"
                      className="rounded-md bg-gray-200 px-3 py-1 text-gray-700 hover:bg-gray-300 dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-gray-600"
                      onClick={onClose}
                    >
                      Cancel
                    </Button>
                    <Button
                      type="submit"
                      id="HelpdeskFormSubmitButton"
                      className="rounded-md bg-orange-500 px-3 py-1 text-white hover:bg-orange-600 dark:bg-orange-600 dark:hover:bg-orange-700"
                      disabled={isSubmitting}
                    >
                      {isSubmitting ? "Submitting..." : "Submit"}
                    </Button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>,
        document.getElementById("modal") as HTMLElement,
      )}
    </div>
  );
};

export default HelpdeskForm;
